import {
  fetchSubscriptionProductsApi,
  verifyCouponAPI,
  createSubscriptionAPI,
  cancelSubscriptionAPI,
} from '../../util/api';
import { decryptNumber } from '../../util/genericHelpers';

const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;

// ================ Action Types ================ //

export const FETCH_SUBSCRIPTION_PRODUCTS_REQUEST =
  'app/SubscriptionPage/FETCH_SUBSCRIPTION_PRODUCTS_REQUEST';
export const FETCH_SUBSCRIPTION_PRODUCTS_SUCCESS =
  'app/SubscriptionPage/FETCH_SUBSCRIPTION_PRODUCTS_SUCCESS';
export const FETCH_SUBSCRIPTION_PRODUCTS_FAILURE =
  'app/SubscriptionPage/FETCH_SUBSCRIPTION_PRODUCTS_FAILURE';

export const VERIFY_COUPON_REQUEST = 'app/SubscriptionPage/VERIFY_COUPON_REQUEST';
export const VERIFY_COUPON_SUCCESS = 'app/SubscriptionPage/VERIFY_COUPON_SUCCESS';
export const VERIFY_COUPON_FAILURE = 'app/SubscriptionPage/VERIFY_COUPON_FAILURE';

export const CREATE_SUBSCRIPTION_REQUEST = 'app/SubscriptionPage/CREATE_SUBSCRIPTION_REQUEST';
export const CREATE_SUBSCRIPTION_SUCCESS = 'app/SubscriptionPage/CREATE_SUBSCRIPTION_SUCCESS';
export const CREATE_SUBSCRIPTION_FAILURE = 'app/SubscriptionPage/CREATE_SUBSCRIPTION_FAILURE';

export const CANCEL_SUBSCRIPTION_REQUEST = 'app/SubscriptionPage/CANCEL_SUBSCRIPTION_REQUEST';
export const CANCEL_SUBSCRIPTION_SUCCESS = 'app/SubscriptionPage/CANCEL_SUBSCRIPTION_SUCCESS';
export const CANCEL_SUBSCRIPTION_FAILURE = 'app/SubscriptionPage/CANCEL_SUBSCRIPTION_FAILURE';

// ================ Initial State ================ //

const initialState = {
  productsLoading: false,
  products: [],
  productsError: null,

  couponLoading: false,
  couponData: null,
  couponError: null,

  subscriptionLoading: false,
  subscription: null,
  subscriptionError: null,

  subscriptionCancelInProgress: false,
  subscriptionCancelResponse: null,
  subscriptionCancelError: null,
};

// ================ Reducer ================ //

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_SUBSCRIPTION_PRODUCTS_REQUEST:
      return {
        ...state,
        productsLoading: true,
        productsError: null,
      };

    case FETCH_SUBSCRIPTION_PRODUCTS_SUCCESS:
      return {
        ...state,
        productsLoading: false,
        products: payload || [],
        productsError: null,
      };

    case FETCH_SUBSCRIPTION_PRODUCTS_FAILURE:
      return {
        ...state,
        productsLoading: false,
        productsError: payload,
      };

    case VERIFY_COUPON_REQUEST:
      return {
        ...state,
        couponLoading: true,
        couponError: null,
      };

    case VERIFY_COUPON_SUCCESS:
      return {
        ...state,
        couponLoading: false,
        couponData: payload,
      };

    case VERIFY_COUPON_FAILURE:
      return {
        ...state,
        couponLoading: false,
        couponError: payload,
      };

    case CREATE_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        subscriptionLoading: true,
        subscriptionError: null,
      };

    case CREATE_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        subscriptionLoading: false,
        subscription: payload,
      };

    case CREATE_SUBSCRIPTION_FAILURE:
      return {
        ...state,
        subscriptionLoading: false,
        subscriptionError: payload,
      };

    case CANCEL_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        subscriptionCancelInProgress: true,
        subscriptionCancelError: null,
      };

    case CANCEL_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        subscriptionCancelInProgress: false,
        subscriptionCancelResponse: payload,
      };

    case CANCEL_SUBSCRIPTION_FAILURE:
      return {
        ...state,
        subscriptionCancelInProgress: false,
        subscriptionCancelError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const subscriptionProductsSelector = state => {
  const slice = state.SubscriptionPage || {};

  return {
    productsLoading: slice.productsLoading || false,
    products: slice.products || [],
    productsError: slice.productsError || null,

    couponLoading: slice.couponLoading || false,
    couponData: slice.couponData || null,
    couponError: slice.couponError || null,

    subscriptionLoading: slice.subscriptionLoading || false,
    subscription: slice.subscription || null,
    subscriptionError: slice.subscriptionError || null,

    subscriptionCancelInProgress: slice.subscriptionCancelInProgress || false,
    subscriptionCancelResponse: slice.subscriptionCancelResponse || null,
    subscriptionCancelError: slice.subscriptionCancelError || null,
  };
};

// ================ Action Creators ================ //

export const fetchSubscriptionProductsRequest = () => ({
  type: FETCH_SUBSCRIPTION_PRODUCTS_REQUEST,
});

export const fetchSubscriptionProductsSuccess = data => ({
  type: FETCH_SUBSCRIPTION_PRODUCTS_SUCCESS,
  payload: data,
});

export const fetchSubscriptionProductsFailure = error => ({
  type: FETCH_SUBSCRIPTION_PRODUCTS_FAILURE,
  payload: error,
});

export const verifyCouponRequest = () => ({
  type: VERIFY_COUPON_REQUEST,
});

export const verifyCouponSuccess = data => ({
  type: VERIFY_COUPON_SUCCESS,
  payload: data,
});

export const verifyCouponFailure = error => ({
  type: VERIFY_COUPON_FAILURE,
  payload: error,
});

export const createSubscriptionRequest = () => ({
  type: CREATE_SUBSCRIPTION_REQUEST,
});

export const createSubscriptionSuccess = data => ({
  type: CREATE_SUBSCRIPTION_SUCCESS,
  payload: data,
});

export const createSubscriptionFailure = error => ({
  type: CREATE_SUBSCRIPTION_FAILURE,
  payload: error,
});

export const cancelSubscriptionRequest = () => ({
  type: CANCEL_SUBSCRIPTION_REQUEST,
});

export const cancelSubscriptionSuccess = data => ({
  type: CANCEL_SUBSCRIPTION_SUCCESS,
  payload: data,
});

export const cancelSubscriptionFailure = error => ({
  type: CANCEL_SUBSCRIPTION_FAILURE,
  payload: error,
});

// ================ Thunks ================ //

export const fetchSubscriptionProducts = () => async dispatch => {
  dispatch(fetchSubscriptionProductsRequest());

  try {
    const response = await fetchSubscriptionProductsApi();
    const products = response?.data || [];
    dispatch(fetchSubscriptionProductsSuccess(products));
    return products;
  } catch (error) {
    dispatch(fetchSubscriptionProductsFailure(error.message || 'Failed to fetch products'));
    return null;
  }
};

export const redeemCoupon = values => async dispatch => {
  dispatch(verifyCouponRequest());

  try {
    const response = await verifyCouponAPI(values);
    const decrypted = decryptNumber(response?.value, encryptionKey);
    response.couponValue = decrypted;
    dispatch(verifyCouponSuccess(response));
    return response;
  } catch (error) {
    dispatch(verifyCouponFailure(error.message));
    return null;
  }
};

export const createSubscription = body => async dispatch => {
  dispatch(createSubscriptionRequest());
  try {
    const response = await createSubscriptionAPI(body);
    dispatch(createSubscriptionSuccess(response));
    return response;
  } catch (error) {
    dispatch(createSubscriptionFailure(error.message || 'Failed to create subscription'));
    return null;
  }
};

export const cancelSubscription = body => async dispatch => {
  dispatch(cancelSubscriptionRequest());
  try {
    const response = await cancelSubscriptionAPI(body);
    dispatch(cancelSubscriptionSuccess(response));
    return response;
  } catch (error) {
    dispatch(cancelSubscriptionFailure(error.message || 'Failed to cancel subscription'));
    return null;
  }
};

// ================ loadData for route ================ //

export const loadData = () => dispatch => {
  return dispatch(fetchSubscriptionProducts());
};
